import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, TextField, Typography } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SearchIcon from "@mui/icons-material/Search";

import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useActionHook, useModals } from "hooks";
import { IApiServiceError, useUserActions } from "store";
import { getDomain, getProtocol } from "utils/auth";
import { AccountForSwitch } from "store/types";

const apiDomain = getDomain();
const protocol = getProtocol();

const isSelected = (accountApiAddress: string) => accountApiAddress === apiDomain;

export const AccountsForSwitch: React.FC = () => {
  const { getAccountForSwitchAction } = useUserActions();

  const { action, data, loading } = useActionHook(getAccountForSwitchAction);
  const { errorModal } = useModals();

  const onSelectAccount = useCallback(async (accountForSwitch: AccountForSwitch) => {
    try {
      // Don't handle same account
      if (isSelected(accountForSwitch.api_domain)) return;

      const { domain: clientUrl } = accountForSwitch;

      const link = document.createElement("a");
      link.href = `${protocol}${clientUrl}`;
      link.click();
    } catch (err) {
      errorModal({ msg: (err as IApiServiceError).message });
    }
  }, []);

  const [search, setSearch] = useState("");

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data;
    return data.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
  }, [data, search]);

  useEffect(() => {
    action();
  }, []);

  return (
    <Stack sx={{ overflow: "auto", overflowX: "hidden", maxWidth: "100%" }}>
      {(data?.length ?? 0) > 10 && (
        <Stack sx={{ p: "8px 12px", flexShrink: 0 }}>
          <TextField
            size="small"
            placeholder="Search account"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ fontSize: 18, color: "#7b92a9", mr: "4px" }} />
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "14px",
                "& fieldset": { borderColor: "#dae0e2" }
              }
            }}
          />
        </Stack>
      )}
      <AsyncContentMui isLoading={loading}>
        {filteredData.map((item) => (
          <Stack
            onClick={() => onSelectAccount(item)}
            key={item.id}
            direction="row"
            sx={{
              height: "50px",
              gap: "12px",
              flexShrink: 0,
              justifyContent: "space-between",
              alignItems: "center",
              p: "0 12px",
              borderBottom: "1px solid #DAE0E2",
              cursor: "pointer",
              overflow: "hidden",
              ...(isSelected(item.api_domain) && { bgcolor: "#F5F7F7" }),
              ":hover": {
                bgcolor: "#F5F7F7"
              }
            }}
          >
            <Stack
              direction="row"
              gap="12px"
              sx={{
                alignItems: "center",
                overflow: "hidden",
                img: {
                  width: "24px",
                  height: "24px",
                  borderRadius: "4px"
                }
              }}
            >
              {item.logo ? <img src={item.logo} /> : <AccountCircleIcon sx={{ width: "24px", height: "24px" }} />}
              <Typography
                sx={{
                  color: "#0A3142",
                  fontSize: "16px",
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden"
                }}
              >
                {item.name}
              </Typography>
            </Stack>
            <Stack
              justifyContent="center"
              sx={{
                borderRadius: "10px",
                bgcolor: "#3A5B9F",
                color: "white",
                border: "1px solid white",
                p: "0 6px",
                minWidth: "20px",
                fontWeight: 500,
                fontSize: "12px"
              }}
            >
              {item.notifications > 99 ? "99+" : item.notifications}
            </Stack>
          </Stack>
        ))}
      </AsyncContentMui>
    </Stack>
  );
};
